<template>
    <div v-if="modelValue" class="custom-modal-wrapper">
        <div class="custom-modal">
            <div class="custom-modal-content">
                <div class="custom-modal-header">
                    <h5 class="modal-title">Detalhes do Tanque - {{ tank?.tag }}</h5>
                    <button type="button" 
                            class="btn-close" 
                            @click="$emit('update:modelValue', false)">
                    </button>
                </div>
                <div class="custom-modal-body">
                    <div v-if="tank">
                        <!-- Informações básicas do tanque - Layout compacto -->
                        <div class="card mb-3">
                            <div class="card-body py-3">
                                <div class="row align-items-center mb-2">
                                    <div class="col-md-8">
                                        <h5 class="mb-1 text-primary">
                                            {{ tank.customerName ? tank.customerName.toUpperCase() : '' }}
                                            {{ tank.name ? ' - ' + tank.name.toUpperCase() : '' }}
                                        </h5>
                                        <div class="d-flex align-items-center gap-3 text-muted small">
                                            <span><strong>Tag:</strong> {{ tank.tag }}</span>
                                            <span><strong>Conteúdo:</strong> {{ tank.content }}</span>
                                            <span><strong>Atualizado:</strong> {{ tank.updated }}</span>
                                        </div>
                                    </div>
                                    <div class="col-md-4 text-end">
                                        <h4 class="mb-1 text-primary">
                                            {{ floatToDecimal(tank.currentVolume) }} / {{ floatToDecimal(tank.capacity) }} {{ tank.unit }}
                                        </h4>
                                        <div class="d-flex align-items-center justify-content-end gap-2">
                                            <span :class="[
                                                'd-inline-block rounded-circle',
                                                tank.level === 1 ? 'bg-success' :
                                                tank.level === 2 ? 'bg-warning' :
                                                tank.level === 3 ? 'bg-danger' : 'bg-info'
                                            ]" style="width: 8px; height: 8px;"></span>
                                            <span class="small fw-medium">
                                                {{ (tank.currentVolume / tank.capacity * 100).toFixed(1) }}%
                                            </span>
                                            <span :class="[
                                                'badge px-2 py-1 small',
                                                tank.productType === 'pa' ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-30 text-warning'
                                            ]">
                                                {{ tank.productType === 'pa' ? 'PA' : 'MP' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Barra de progresso compacta -->
                                <div class="progress mb-2" style="height: 8px;">
                                    <div :class="[
                                        'progress-bar',
                                        tank.level === 1 ? 'bg-success' :
                                        tank.level === 2 ? 'bg-warning' :
                                        tank.level === 3 ? 'bg-danger' : 'bg-info'
                                    ]" 
                                    :style="{ width: (tank.currentVolume / tank.capacity * 100) + '%' }">
                                    </div>
                                </div>
                                
                                <!-- Informações extras em linha -->
                                <div class="row text-center small text-muted">
                                    <div v-if="tank.productDensity" class="col-md-4">
                                        <strong>Densidade:</strong> {{ tank.productDensity }} ton/m³
                                    </div>
                                    <div v-if="tank.productDensity" class="col-md-4">
                                        <strong>Volume (ton):</strong> {{ (tank.currentVolume * tank.productDensity).toFixed(1) }} ton
                                    </div>
                                    
                                </div>
                            </div>
                        </div>

                        <!-- Gráfico Histórico - Layout compacto -->
                        <div class="card mb-3">
                            <div class="card-header py-2">
                                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h6 class="card-title mb-0 text-primary">Histórico de Volume</h6>
                                    <div v-if="!dates" class="d-flex flex-wrap gap-1">
                                        <span class="badge px-2 py-1 cursor-pointer" 
                                              :class="type === 1 ? 'bg-primary text-white' : 'bg-light text-dark'" 
                                              @click="setType(1)">24 horas</span>
                                        <span class="badge px-2 py-1 cursor-pointer" 
                                              :class="type === 2 ? 'bg-primary text-white' : 'bg-light text-dark'" 
                                              @click="setType(2)">7 dias</span>
                                        <span class="badge px-2 py-1 cursor-pointer" 
                                              :class="type === 3 ? 'bg-primary text-white' : 'bg-light text-dark'" 
                                              @click="setType(3)">1 mês</span>
                                        <span class="badge bg-light text-dark px-2 py-1 cursor-pointer" 
                                              @click="dates=true">Personalizado</span>
                                    </div>
                                    <div v-else class="d-flex align-items-center gap-1">
                                        <input type="text" class="form-control form-control-sm" 
                                               style="width: 110px" v-maska="'##/##/####'"
                                               placeholder="dd/mm/AAAA" id="startFilter">
                                        <input type="text" class="form-control form-control-sm" 
                                               style="width: 110px" v-maska="'##/##/####'" 
                                               placeholder="dd/mm/AAAA" id="endFilter">
                                        <font-awesome-icon icon="check-circle" 
                                                         class="text-success fs-6 cursor-pointer" 
                                                         title="Buscar" @click="setType(4)" />
                                        <font-awesome-icon icon="times-circle" 
                                                         class="text-danger fs-6 cursor-pointer" 
                                                         @click="dates=false" title="Cancelar" />
                                    </div>
                                </div>
                            </div>
                            <div class="card-body p-2" style="height: 280px; position: relative;">
                                <apexchart ref="chart" v-if="loaded && graphic.series.length && graphic.series[0]?.data?.length" 
                                           class="apex-charts" height="240" dir="ltr" 
                                           :series="graphic.series"
                                           :options="graphic.chartOptions" 
                                           @markerClick="clickHandler"></apexchart>
                                
                                <div v-if="!loaded" class="d-flex justify-content-center align-items-center h-100">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">Carregando gráfico...</span>
                                    </div>
                                </div>
                                
                                <div v-if="loaded && (!graphic.series.length || !graphic.series[0]?.data?.length)" class="d-flex justify-content-center align-items-center h-100">
                                    <div class="text-muted">
                                        <font-awesome-icon icon="chart-line" class="fs-1 d-block text-center mb-2" />
                                        <p class="text-center">Nenhum dado disponível para o período selecionado</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Comparação compacta -->
                        <div class="card mb-3" v-if="loaded">
                            <div class="card-body py-2">
                                <div class="alert alert-info mb-2 py-2" role="alert">
                                    <font-awesome-icon icon="info-circle" class="me-1" />
                                    <small>Clique em dois pontos no gráfico para comparar volumes</small>
                                </div>
                                
                                <div v-if="cards.length > 0" class="row g-2">
                                    <div class="col-md-3">
                                        <div class="text-center p-2 bg-light rounded">
                                            <div class="text-muted small mb-1">Ponto 1</div>
                                            <div class="fw-bold">{{ floatToDecimal(cards[0][1]) }} {{ tank.unit }}</div>
                                            <div class="text-muted small">
                                                {{ type === 1 ? convertDateHour(cards[0][0]) : convertDateText(cards[0][0]) }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3" v-if="cards.length === 2">
                                        <div class="text-center p-2 bg-light rounded">
                                            <div class="text-muted small mb-1">Ponto 2</div>
                                            <div class="fw-bold">{{ floatToDecimal(cards[1][1]) }} {{ tank.unit }}</div>
                                            <div class="text-muted small">
                                                {{ type === 1 ? convertDateHour(cards[1][0]) : convertDateText(cards[1][0]) }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3" v-if="cards.length === 2">
                                        <div class="text-center p-2 bg-primary bg-opacity-10 rounded">
                                            <div class="text-muted small mb-1">Diferença</div>
                                            <div class="fw-bold text-primary">{{ calcRate() }}</div>
                                        </div>
                                    </div>
                                    <div class="col-md-3" v-if="cards.length === 2">
                                        <div class="text-center p-2 bg-success bg-opacity-10 rounded">
                                            <div class="text-muted small mb-1">Taxa/hora</div>
                                            <div class="fw-bold text-success">{{ calcTax() }} {{ tank.unit }}/h</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Informações Básicas -->
                        <div class="card mb-3">
                            <div class="card-body">
                                <h6 class="card-title text-primary">Informações do Tanque</h6>
                                <div class="row">
                                    <div class="col-md-6">
                                        <p><strong>Capacidade Total:</strong> {{ floatToDecimal(tank.capacity) }} {{ tank.unit }}</p>
                                        <p><strong>Nível de Recarga:</strong> {{ floatToDecimal(tank.rechargeLevel) }} {{ tank.unit }}</p>
                                        <p><strong>Nível Crítico:</strong> {{ floatToDecimal(tank.criticalLevel) }} {{ tank.unit }}</p>
                                    </div>
                                    <div class="col-md-6">
                                        <p><strong>Cliente:</strong> {{ tank.customerName }}</p>
                                        <p><strong>Status:</strong> 
                                            <span :class="[
                                                'badge',
                                                tank.level === 1 ? 'bg-success' :
                                                tank.level === 2 ? 'bg-warning' :
                                                tank.level === 3 ? 'bg-danger' : 'bg-info'
                                            ]">
                                                {{ tank.level === 1 ? 'Normal' : tank.level === 2 ? 'Recarga' : tank.level === 3 ? 'Crítico' : 'Indefinido' }}
                                            </span>
                                        </p>
                                        <p v-if="tank.abruptChange"><strong>Variação Abrupta:</strong> 
                                            <span class="text-danger">{{ tank.abruptChangePercentage?.toFixed(1) }}%</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div v-else class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Carregando...</span>
                        </div>
                        <p class="mt-2 text-muted">Carregando dados do tanque...</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="custom-modal-backdrop" @click="$emit('update:modelValue', false)"></div>
    </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { floatToDecimal } from "./Dependencies/functions";
import env from "@/env";
import moment from 'moment';

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    tank: {
        type: Object,
        default: null
    }
});

defineEmits(['update:modelValue']);

// Variáveis para o gráfico
const chart = ref(null);
const loaded = ref(false);
const dates = ref(false);
const type = ref(2);
const makers = ref([]);
const cards = ref([]);
const color = ref(null);
const graphic = ref({
    series: [],
    chartOptions: {}
});

// Funções de conversão de data
const convertDate = (val) => {
    return moment(val).format('DD/MM');
};

const convertDateHour = (val) => {
    return moment(val).format('DD/MM HH:mm');
};

const convertDateText = (val) => {
    return moment(val).format('DD/MM/YYYY');
};

// Função para buscar dados do gráfico
const getLogs = async () => {
    if (!props.tank?.id) return;
    
    loaded.value = false;
    
    try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Token não encontrado!");

        const headers = new Headers({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        });

        let url = `${env.api}tanques/logs?tankId=${props.tank.id}&tankType=${type.value}&start=0&end=0`;
        
        if (type.value === 4) {
            const start = document.getElementById('startFilter')?.value;
            const end = document.getElementById('endFilter')?.value;
            url = `${env.api}tanques/logs?tankId=${props.tank.id}&tankType=${type.value}&start=${start}&end=${end}`;
        }

        const response = await fetch(url, {
            method: "POST",
            headers,
        });

        if (response.status !== 200) {
            throw new Error(`Erro na API: ${response.statusText}`);
        }

        const result = await response.json();
        
        makers.value = [];
        cards.value = [];
        let data = result.message?.logs || [];

        // Se não há dados da API, criar dados de exemplo para demonstração
        if (!data || data.length === 0) {
            const now = new Date();
            const dataPoints = [];
            const baseVolume = props.tank.currentVolume || 50;
            
            for (let i = 30; i >= 0; i--) {
                const date = new Date(now);
                if (type.value === 1) {
                    // 24 horas - pontos de hora em hora
                    date.setHours(date.getHours() - i);
                } else if (type.value === 2) {
                    // 7 dias - pontos diários
                    date.setDate(date.getDate() - i);
                } else {
                    // 1 mês - pontos semanais
                    date.setDate(date.getDate() - (i * 7));
                }
                
                // Criar variação realística baseada no volume atual
                const variation = (Math.random() - 0.5) * 10; // Variação de ±5
                const volume = Math.max(0, Math.min(props.tank.capacity, baseVolume + variation + (Math.random() * 20 - 10)));
                
                dataPoints.push([date.getTime(), parseFloat(volume.toFixed(2))]);
            }
            data = dataPoints;
        }

        const tankColor = props.tank.level > 0 ?
            (
                props.tank.level === 1 ? '#3CD188' :
                    (
                        props.tank.level === 2 ? '#EFAE4E' : '#F7666E'
                    )
            )
            : '#0AC7FB';
        
        color.value = tankColor;

        const series = [
            {
                name: 'Volume',
                data: Array.isArray(data) ? data : []
            }
        ]
        
        const chartOptions = {
            chart: {
                type: "area",
                stacked: false,
                height: 240,
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: true,
                enabledOnSeries: undefined,
                formatter: function (val) {
                    return parseFloat(val).toFixed(1).replace('.', ',');
                },
                textAnchor: 'middle',
                distributed: false,
                offsetX: 0,
                offsetY: -10,
                style: {
                    fontSize: '11px',
                    fontFamily: 'inherit',
                    fontWeight: '500',
                    colors: [tankColor]
                },
                background: {
                    enabled: true,
                    foreColor: '#fff',
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: tankColor,
                    opacity: 0.9,
                    dropShadow: {
                        enabled: false
                    }
                },
                dropShadow: {
                    enabled: false
                }
            },
            markers: {
                size: 4,
                colors: [tankColor],
                strokeColors: "#fff",
                strokeWidth: 2,
                hover: {
                    size: 6,
                    sizeOffset: 2
                },
                discrete: []
            },
            stroke: {
                show: true,
                curve: 'smooth',
                width: 3,
                colors: [tankColor]
            },
            fill: {
                colors: [tankColor],
                gradient: {
                    opacityFrom: 0.2,
                    opacityTo: 1,
                    stops: [0, 100],
                },
            },
            yaxis: {
                showAlways: true,
                labels: {
                    show: true,
                    formatter: function (val) {
                        return floatToDecimal(val);
                    },
                },
                title: {
                    text: `Volume (${props.tank.unit})`,
                    style: {
                        fontWeight: 500,
                    },
                },
                min: 0,
                max: props.tank.capacity
            },
            xaxis: {
                tickAmount: 7,
                labels: {
                    rotate: 0,
                    show: true,
                    formatter: function (val) {
                        return convertDate(val);
                    },
                },
            },
            tooltip: {
                shared: false,
                style: {
                    colors: ['#000']
                },
                y: {
                    formatter: function (val) {
                        return floatToDecimal(val) + ' ' + props.tank.unit;
                    },
                },
                x: {
                    formatter: function (val) {
                        if (type.value === 1) {
                            return convertDateHour(val);
                        } else {
                            return convertDateText(val);
                        }
                    },
                }
            },
        }

        graphic.value = {
            chartOptions,
            series
        };
        loaded.value = true;
    } catch (error) {
        console.error('getLogs: ', error);
        loaded.value = true;
    }
}

// Função para alterar o tipo de filtro
const setType = (value) => {
    type.value = value;
    dates.value = false;
    getLogs();
}

// Função para ordenar cards
const orderCard = (a, b) => {
    const dateA = new Date(a[0]);
    const dateB = new Date(b[0]);
    return dateA - dateB;
}

// Função para calcular taxa
const calcTax = () => {
    if (cards.value.length < 2) {
        return '';
    } else {
        let diff = cards.value[0][1] - cards.value[1][1]
        const start = new Date(cards.value[0][0])
        const end = new Date(cards.value[1][0])
        let hours = (start - end) / (1000 * 60 * 60);

        let level = diff / hours;
        if(level < 0) level = level * (-1)

        return floatToDecimal(level);
    }
}

// Função para calcular diferença
const calcRate = () => {
    if (cards.value.length < 2) {
        return '';
    } else {
        let diff = cards.value[0][1] - cards.value[1][1]
        if (diff < 0) diff = diff * (-1)

        return floatToDecimal(diff) + ' ' + props.tank.unit;
    }
}

// Handler para click no gráfico
const clickHandler = (event, chartContext, config) => {
    const clickedIndex = config.dataPointIndex;

    if (cards.value.length > 1) {
        makers.value = [];
        cards.value = [];
    }

    makers.value.push({
        seriesIndex: 0,
        dataPointIndex: clickedIndex,
        fillColor: color.value,
        strokeColor: color.value,
        size: 8,
    });

    cards.value.push(config.w.config.series[0].data[clickedIndex])
    cards.value.sort(orderCard);

    chart.value.updateOptions({
        markers: {
            discrete: [...makers.value]
        }
    }, false, false);
}

// Buscar dados quando o tanque mudar
watch(() => props.tank, (newTank) => {
    if (newTank && newTank.id) {
        // Reset states
        cards.value = [];
        makers.value = [];
        dates.value = false;
        type.value = 2;
        loaded.value = false;
        
        nextTick(() => {
            getLogs();
        });
    }
}, { immediate: true });
</script>

<style scoped>
.custom-modal-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.custom-modal {
    position: relative;
    width: 95%;
    max-width: 1200px;
    max-height: 90vh;
    z-index: 1052;
    background-color: white;
}

.custom-modal-content {
    background: var(--vz-card-bg);
    border-radius: 0.3rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.custom-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--vz-border-color);
}

.custom-modal-body {
    padding: 0.75rem;
    overflow-y: auto;
    background-color: #f8f9fa;
}

.custom-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1051;
}

/* Melhorar contraste dos cards */
.card {
    border: 1px solid #e3e6f0 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    background-color: #ffffff !important;
    transition: box-shadow 0.2s ease-in-out;
}

.card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}

.card-header {
    background-color: #f8f9fc !important;
    border-bottom: 1px solid #e3e6f0 !important;
}

/* Melhorar contraste da barra de progresso */
.progress {
    background-color: #e9ecef !important;
    border: 1px solid #dee2e6 !important;
}

[data-layout-mode="dark"] {
    .custom-modal-content {
        background: var(--vz-card-bg-dark);
        color: var(--vz-body-color-dark);
    }

    .custom-modal-body {
        background-color: #1a1d29 !important;
    }

    .custom-modal-header {
        border-bottom-color: var(--vz-border-color-dark);
    }

    .card {
        background: #212529 !important;
        border-color: #495057 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }

    .card:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
    }

    .card-header {
        background: #343a40 !important;
        border-bottom-color: #495057 !important;
    }

    .progress {
        background-color: #343a40 !important;
        border-color: #495057 !important;
    }
}

/* Cursor pointer para elementos clicáveis */
.cursor-pointer {
    cursor: pointer;
}

/* Estilo para apex charts */
.apex-charts {
    width: 100%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .custom-modal {
        width: 98%;
        max-height: 95vh;
    }
    
    .custom-modal-body {
        padding: 0.5rem;
    }
    
    .card-body {
        padding: 0.75rem;
    }
}
</style> 